import React, { useEffect, useState, useContext } from 'react'
import getOr from 'lodash/fp/getOr'
import pick from 'lodash/fp/pick'
import isEmpty from 'lodash/fp/isEmpty'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { MdAccountCircle, MdSave, MdLock, MdModeEdit } from 'react-icons/md'

import AppContext from '../../context'
import { updateLocalToken } from '../../auth/helpers'
import { SimpleLoader, KnTField } from '../../components'
import { ImageCropModal } from '../../components/ImageCropModal'
import {
  useCurrentUser,
  simpleAlert,
  confirmAlert,
  useImageUpload,
} from '../../common'
import { createImageUrl } from '../../common/imageFunctions'
import { gray } from '../../styles/themeColors'
import { useForm } from '../../common/alterForms'
import { Error } from '../../types'
import {
  GET_MY_MAIN_INFORMATION,
  UPDATE_USER,
  RESET_PASSWORD_LINK,
} from '../../graphql'
import { myHub } from '../../common/constants'
import { useAppDispatch } from '../../hooks'
import { updateImageVersion } from '../../features/siteSlice'

import { Form } from './style'

export const ManageProfile: React.FC = () => {
  const history = useHistory()
  const [loaded, setLoaded] = useState(false)
  const { userId } = useCurrentUser()
  const { state, auth } = useContext(AppContext)
  const dispatch = useAppDispatch()

  const [updateUser, { loading: loadingUpdateUser }] = useMutation(UPDATE_USER)
  const [submitResetLink] = useMutation(RESET_PASSWORD_LINK)

  const { data, loading } = useQuery(GET_MY_MAIN_INFORMATION)
  const init = { firstName: '', lastName: '', avatarPath: '' }
  const { model, errors, handleFieldChange, reset, pushError } = useForm(init)
  const me = getOr({}, 'me', data)

  const imageUpload = useImageUpload({
    path: 'avatars',
    name: userId,
    aspectRatio: 1,
    onSuccess: () => {
      dispatch(updateImageVersion())
      reset({ ...model, avatarPath: 'avatars' })
    },
  })

  useEffect(() => {
    if (!loaded && !isEmpty(me)) {
      reset({
        firstName: me.firstName,
        lastName: me.lastName,
        avatarPath: '',
      })

      setLoaded(true)
    }
  }, [me, reset, loaded])

  useEffect(() => {
    if (userId) {
      imageUpload.setImagePreview(
        createImageUrl({ path: `avatars/${userId}.jpg` })
      )
      imageUpload.setImageLoadError(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  if (loading) {
    return <SimpleLoader />
  }

  const updatePersonalData = (ev: React.FormEvent) => {
    ev.preventDefault()
    if (loadingUpdateUser) return
    const currentUser = pick(['userId', 'role'], state.currentUser)
    const variables = {
      input: {
        firstName: model.firstName,
        lastName: model.lastName,
      },
      userId,
    }
    updateUser({ variables }).then(response => {
      if (response.data.updateUser.errors.length > 0) {
        response.data.updateUser.errors.forEach(({ key, message }: Error) => {
          pushError(message, key)
        })
      } else {
        // updating context and token
        const { result } = response.data.updateUser
        const userUpdated = {
          ...currentUser,
          firstName: result.firstName,
          lastName: result.lastName,
          email: result.email,
        }
        updateLocalToken({ user: userUpdated })
        auth.updateUserData(userUpdated)
        // end
        simpleAlert({
          html: 'Your data has been successfully updated',
          icon: 'success',
        })
        history.push(myHub)
      }
    })
  }

  const updatePassword = () => {
    confirmAlert({
      html: 'do you want to start the process to reset your password? We will send you an email with instructions.',
      icon: 'question',
      confirmButtonText: 'Continue',
    }).then(response => {
      if (response.value) {
        submitResetLink().then(({ data: { passwordResetLink } }) => {
          if (passwordResetLink.errors.length > 0) {
            simpleAlert({
              html: 'There was a problem sending your email',
              icon: 'error',
            })
          } else {
            simpleAlert({
              html: 'A password reset email was sent to your email',
              icon: 'success',
            })
          }
        })
      }
    })
  }

  return (
    <Card
      className="shadow-sm mx-auto py-4 my-4 my-lg-5"
      style={{ maxWidth: '585px', width: '96%' }}
    >
      <Helmet>
        <title>Manage Profile - Kiss & Tell</title>
        <meta
          name="description"
          content="Update your personal information and change your password"
        />
      </Helmet>
      <div>
        <h2 className="text-center fz-24 fwb">Manage Profile</h2>
        <Form onSubmit={updatePersonalData}>
          <div className="manage-profile-picture">
            {imageUpload.imagePreview && !imageUpload.imageLoadError ? (
              <img
                src={imageUpload.imagePreview}
                alt={`${model.firstName} ${model.lastName}`}
                onError={() => imageUpload.setImageLoadError(true)}
              />
            ) : (
              <MdAccountCircle size={138} color={gray} />
            )}
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={imageUpload.handleFileSelect}
              style={{ display: 'none' }}
              id="avatar-upload"
              disabled={imageUpload.uploading}
            />
            <Button
              as="label"
              htmlFor="avatar-upload"
              className="ml-3"
              variant="outline-secondary"
              disabled={imageUpload.uploading}
            >
              <MdModeEdit size={24} className="mr-1" />
              {imageUpload.uploading ? 'Uploading...' : 'Edit photo'}
            </Button>
          </div>
          {imageUpload.uploadImageMsg && (
            <span className="d-block fz-11 ml-3 text-primary text-center">
              {imageUpload.uploadImageMsg}
            </span>
          )}
          <KnTField
            label="First name"
            value={model}
            error={errors}
            setValue={handleFieldChange}
            name="firstName"
            placeholder="Enter your first name"
            icon={<MdAccountCircle size={24} color={gray} />}
          />
          <KnTField
            label="Last name"
            value={model}
            error={errors}
            setValue={handleFieldChange}
            name="lastName"
            placeholder="Enter your last name"
            icon={<MdAccountCircle size={24} color={gray} />}
          />
          <div className="text-right py-4">
            <Button type="button" onClick={updatePassword}>
              <MdLock color="white" size={26} /> Change Password
            </Button>
            <Button type="submit" className="ml-2">
              <MdSave color="white" size={26} className="mr-1" /> Save
            </Button>
          </div>
        </Form>
      </div>
      {imageUpload.selectedImage && (
        <ImageCropModal
          show={imageUpload.showCropModal}
          imageSrc={imageUpload.selectedImage}
          onHide={() => {
            imageUpload.setShowCropModal(false)
            imageUpload.setSelectedImage(null)
          }}
          onCropComplete={imageUpload.handleCropComplete}
          aspectRatio={imageUpload.aspectRatio}
        />
      )}
    </Card>
  )
}
